import {Component, OnDestroy, OnInit, computed, inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CoreService} from '../../../core/services/core.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CoreEventsService} from '../../../core/services/core-events.service';
import {Appointment, AppointmentType, pickerToWallClock} from '@xmedical/core';
import {PatientAppointmentsStore, PatientStore} from '../../../core/state';
import dayjs from 'dayjs/esm';

// Fallback when the practice has not configured a default appointment length.
const FALLBACK_DURATION_MINUTES = 30;

interface AppointmentYearGroup {
  year: number;
  appointments: Appointment[];
  isExpanded: boolean;
}

@Component({
    selector: 'app-patient-appointments-widget',
    templateUrl: './appointments-widget.component.html',
    styleUrls: ['./appointments-widget.component.scss'],
    standalone: false
})
export class AppointmentsWidgetComponent implements OnInit, OnDestroy {


  error = '';
  saving = false;
  appointmentTypes: AppointmentType[] = [];
  form: UntypedFormGroup = null;

  private patientStore = inject(PatientStore);
  protected appointmentsStore = inject(PatientAppointmentsStore);

  readonly appointments = computed(() => this.appointmentsStore.items());
  readonly loading = computed(() => this.appointmentsStore.loading());

  showGroupedView = computed(() => this.appointments().length > 10);

  appointmentsByYear = computed<AppointmentYearGroup[]>(() => {
    const appointments = this.appointments();
    if (appointments.length === 0) return [];

    const groupedByYear = new Map<number, Appointment[]>();
    for (const appointment of appointments) {
      const year = new Date(appointment.start).getFullYear();
      if (!groupedByYear.has(year)) groupedByYear.set(year, []);
      groupedByYear.get(year)!.push(appointment);
    }

    const years = Array.from(groupedByYear.keys()).sort((a, b) => b - a);
    return years.map((year, index) => ({
      year,
      appointments: groupedByYear.get(year)!,
      isExpanded: index === 0
    }));
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private coreService: CoreService,
    private coreEventService: CoreEventsService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      appointmentDate: [null, Validators.required], // quirk to fix invalid date error on form. if value is null, date is valid, if value is empty string, its not a valid DATE so the field is marked invalid
      bookingComment: ['', Validators.maxLength(250)]
    });
    this.subscriptions.push(this.coreEventService.events$.pipe(filter(event => event.type === 'appointment_types_loaded')).subscribe(_ =>
      this.appointmentTypes = this.coreService.appointmentTypes().filter(t => t.isPatientAppointment).sort((a, b) => a.id > b.id ? 1 : -1)));
    this.appointmentTypes = this.coreService.appointmentTypes()?.filter(t => t.isPatientAppointment).sort((a, b) => a.id > b.id ? 1 : -1) ?? [];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  get hasError(): boolean {
      return this.error.length > 0;
  }

  get hasAppointments(): boolean {
      return this.appointments().length > 0 && this.appointmentTypes.length > 0;
  }

  /** Practice's configured default appointment length, falling back to 30 minutes. */
  private get defaultDurationMinutes(): number {
    return this.coreService.practiceSettings()?.defaultAppointmentDurationMinutes ?? FALLBACK_DURATION_MINUTES;
  }

  /** Default booking location: the practice's default, else the first bookable one. */
  private get defaultLocationId(): number | undefined {
    const locations = this.coreService.practiceLocations().filter(location => !location.isDeleted);
    const location = locations.find(l => l.isDefault) ?? locations[0];
    return location?.id;
  }

  /** Default practitioner: the practice's primary, else the first active one. */
  private get defaultPractitionerId(): number | undefined {
    const practitioners = this.coreService.practicePractitioners().filter(practitioner => !practitioner.isDeleted);
    const practitioner = practitioners.find(p => p.isPrimary) ?? practitioners[0];
    return practitioner?.id;
  }

  saveAppointment() {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.saving = true;
    const field = this.form.get('appointmentDate') as UntypedFormControl;
    const date = field.value;
    // Appointment start/end are stored as zone-less practice wall-clock. The picker's
    // value already holds the wall the user picked, so send it verbatim via
    // pickerToWallClock — never `new Date(date)`, which serialises a UTC instant and
    // lands the appointment 2 hours early in SAST (UTC+2). End is start + the practice's
    // default duration, computed on the wall-clock so the offset can't creep back in.
    const end = dayjs(date).add(this.defaultDurationMinutes, 'minute');
    const appointment = {
      title: patient.formalName,
      start: pickerToWallClock(date),
      end: pickerToWallClock(end.toDate()),
      comment: '',
      practiceId: this.coreService.selectedPracticeId(),
      practiceLocationId: this.defaultLocationId,
      practicePractitionerId: this.defaultPractitionerId,
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdDateUtc: new Date(),
      modifiedDateUtc: new Date(),
      createdUser: this.coreService.currentUser,
      lastModifiedUser: this.coreService.currentUser,
      deleted: false,
      appointmentType: this.appointmentTypes[0]
    } as Appointment;
    this.error = '';
    this.subscriptions.push(this.appointmentsStore.add(appointment).subscribe({
      next: () => {
        field.setValue(null);
        this.saving = false;
      },
      error: () => {
        this.error = 'Could not save your new appointment';
        field.setValue(null);
        this.saving = false;
      }
    }));
  }

}
